import { AddNoteWrapper, HeaderWrapper, NoteCard, NoteHeader, NoteMeta, NotesList, NotesWrapper, StackArea, StackFrontCard, StackLayer } from './appointment-details-notes.style'
import { Button, Input, Popconfirm, Typography, theme } from 'antd'
import { DeleteOutlined, DownOutlined, UpOutlined } from '@ant-design/icons'
import { Label } from 'components/styled'
import { Avatar } from 'components/avatar/avatar'
import { useAddAppointmentNote, useConfig, useCurrentLocation, useDeleteAppointmentNote } from 'api'
import { FormEvent, useEffect, useState } from 'react'
import { nameShortener, queryClient, useTouchDetector, useTranslate } from 'utils'
import { AppointmentNote } from 'types'
const { Text, Paragraph } = Typography

//the backend caps an appointment at 10 notes
const MAX_APPOINTMENT_NOTES = 10

//an appointment can hold up to 10 notes; only this many stacked "pages" are drawn behind the top one
const MAX_STACK_LAYERS = 3

type Props = {
  notes: AppointmentNote[]
  appointmentId: string
  timezone?: string | null
}

export const AppointmentDetailsNotes = ({ notes, appointmentId, timezone }: Props) => {

  //hooks:
  const { __ } = useTranslate()
  const { data: config } = useConfig()
  const { data: currentLocation } = useCurrentLocation()
  const { isTouchscreen } = useTouchDetector()
  const { token: { fontSizeSM, colorText, colorTextDescription, colorTextSecondary, colorTextTertiary } } = theme.useToken()

  //state:
  const [draft, setDraft] = useState('')
  const [isExpanded, setIsExpanded] = useState(false)
  const [hoveredNoteId, setHoveredNoteId] = useState<string | null>(null)
  const [focusedNoteId, setFocusedNoteId] = useState<string | null>(null)

  //add note (the text comes from the current draft, baked into the mutation variables):
  const { mutate: addNote, isLoading: isAdding } = useAddAppointmentNote(appointmentId, draft.trim())

  //the effective timezone: explicit prop wins, otherwise the selected location's tz, otherwise the account default
  //(same one-liner used in appointment-details, booking-page, calendar-page-calendar, ...)
  const effectiveTimezone = timezone ?? (currentLocation ? currentLocation.timezone : config ? config.settings.timezone : null)

  //newest first for display (the list is kept in the server's ascending order, so reverse it):
  const sortedNotes = [...notes].reverse()
  const isMaxedOut = sortedNotes.length >= MAX_APPOINTMENT_NOTES

  //formatDateTime: renders the UTC `createdAt` in the effective timezone
  const formatDateTime = (createdAt: string) =>
    new Date(createdAt).toLocaleString(
      [config && config.locale, 'en-US'].filter(Boolean) as string[],
      {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: effectiveTimezone ?? undefined
      }
    )

  //submit: add the note. the draft is cleared on success (not here) so the in-flight mutation keeps the
  //typed text — the hook bakes `draft` into its variables at render time. on failure the form is left as-is
  //(the API layer surfaces the error) so the user can retry
  const submit = () => {
    const text = draft.trim()
    if(!text || isMaxedOut) return
    addNote(undefined, {
      onSuccess: () => {
        queryClient.invalidateQueries(['appointment', appointmentId])
        queryClient.invalidateQueries(['appointments'])
        setDraft('')
      }
    })
  }

  //handleSubmit:
  const handleSubmit = (event: FormEvent) => {
    event.preventDefault()
    submit()
  }

  //renderMeta: the avatar + author + timestamp line shared by the collapsed and expanded views
  const renderMeta = (note: AppointmentNote) =>
    <NoteMeta>
      <Avatar
        size='nano'
        image={note.author?.avatar ?? null}
        placeholder={nameShortener(note.author?.name) ?? '?'}
        style={{ padding: 0, flexShrink: 0 }}
      />
      {note.author?.name ? (
        <>
          <Text style={{ fontSize: fontSizeSM, color: colorTextTertiary, flex: '0 1 auto', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {note.author.name}
          </Text>
          <Text style={{ fontSize: fontSizeSM, color: colorTextTertiary }}>·</Text>
        </>
      ) : null}
      <Text style={{ fontSize: fontSizeSM, color: colorTextSecondary, whiteSpace: 'nowrap', flexShrink: 0 }}>
        {formatDateTime(note.createdAt)}
      </Text>
    </NoteMeta>

  const newest = sortedNotes[0]
  const stackLayerCount = Math.min(sortedNotes.length - 1, MAX_STACK_LAYERS)
  const isCollapsedStack = sortedNotes.length > 1 && !isExpanded

  return (
    <NotesWrapper>

      {/* header */}
      <HeaderWrapper>
        <Text style={{ fontFamily: 'inter-medium' }}>{__`notes`}</Text>
        {sortedNotes.length > 0 && (
          <Label
            text={`${sortedNotes.length}`}
            type='info'
            size='tiny'
            style={{ minWidth: 24, display: 'inline-flex', justifyContent: 'center' }}
          />
        )}
      </HeaderWrapper>

      {/* add note (or the limit message once the 10-note cap is reached) */}
      {isMaxedOut ? (
        <Paragraph style={{ fontSize: fontSizeSM, color: colorTextDescription, margin: 0 }}>
          {__('notes_limit_reached', { max: MAX_APPOINTMENT_NOTES })}
        </Paragraph>
      ) : (
        <AddNoteWrapper onSubmit={handleSubmit}>
          <Input.TextArea
            value={draft}
            onChange={event => setDraft(event.target.value)}
            placeholder={__`add_a_note`}
            disabled={isAdding}
            autoSize={{ minRows: 1, maxRows: 3 }}
            onPressEnter={event => {
              if(event.shiftKey){
                event.preventDefault()
                submit()
              }
            }}
          />
          <Button type='primary' htmlType='submit' loading={isAdding} disabled={!draft.trim() || isAdding}>
            {__`add`}
          </Button>
        </AddNoteWrapper>
      )}

      {/* empty state */}
      {sortedNotes.length === 0 && (
        <Paragraph style={{ fontSize: fontSizeSM, color: colorTextDescription, margin: 0 }}>
          {__`no_notes_yet`}
        </Paragraph>
      )}

      {/* collapsed: stacked pages peeking behind the newest note */}
      {isCollapsedStack && (
        <StackArea $peekSize={stackLayerCount} onClick={() => setIsExpanded(true)}>
          {Array.from({ length: stackLayerCount }).map((_, index) => (
            <StackLayer key={index} $index={stackLayerCount - index} $peekSize={stackLayerCount} />
          ))}
          <StackFrontCard>
            <Paragraph style={{ fontSize: fontSizeSM, color: colorText, margin: 0 }}>
              {newest.text}
            </Paragraph>
            <NoteHeader>
              {renderMeta(newest)}
            </NoteHeader>
          </StackFrontCard>
        </StackArea>
      )}

      {/* collapsed: expand affordance */}
      {isCollapsedStack && (
        <Button type='text' size='small' style={{ width: '100%' }} onClick={() => setIsExpanded(true)}>
          {__`show_more`} ({sortedNotes.length - 1}) <DownOutlined style={{ fontSize: fontSizeSM }} />
        </Button>
      )}

      {/* expanded list (also used when there is a single note) */}
      {(isExpanded || sortedNotes.length <= 1) && sortedNotes.length > 0 && (
        <NotesList>
          {sortedNotes.map(note => (
            <NoteCard
              key={note.id}
              onMouseEnter={() => setHoveredNoteId(note.id)}
              onMouseLeave={() => setHoveredNoteId(null)}
              onFocus={() => setFocusedNoteId(note.id)}
              onBlur={() => setFocusedNoteId(null)}
            >
              <Paragraph style={{ fontSize: fontSizeSM, color: colorText, margin: 0 }}>
                {note.text}
              </Paragraph>
              <NoteHeader>
                {renderMeta(note)}
                <NoteDeleteButton
                  appointmentId={appointmentId}
                  noteId={note.id}
                  isVisible={hoveredNoteId === note.id || focusedNoteId === note.id || isTouchscreen}
                />
              </NoteHeader>
            </NoteCard>
          ))}
        </NotesList>
      )}

      {/* expanded: collapse affordance */}
      {isExpanded && sortedNotes.length > 1 && (
        <Button type='text' size='small' style={{ width: '100%' }} onClick={() => setIsExpanded(false)}>
          {__`show_less`} <UpOutlined style={{ fontSize: fontSizeSM }} />
        </Button>
      )}

    </NotesWrapper>
  )
}

type NoteDeleteButtonProps = {
  appointmentId: string
  noteId: string
  isVisible: boolean
}

//each note owns its own delete mutation (the noteId is baked in, like useRemoveCustomer(id))
const NoteDeleteButton = ({ appointmentId, noteId, isVisible }: NoteDeleteButtonProps) => {

  //hooks:
  const { __ } = useTranslate()
  const { token: { colorTextTertiary } } = theme.useToken()
  const { mutate, isLoading, data: response } = useDeleteAppointmentNote(appointmentId, noteId)

  //after delete: refetch the appointment so the note disappears
  useEffect(() => {
    if(response){
      queryClient.invalidateQueries(['appointment', appointmentId])
      queryClient.invalidateQueries(['appointments'])
    }
  }, [response])

  return (
    <Popconfirm
      title={__`are_you_sure_you_want_to_delete_this_note`}
      okText={__`delete`}
      cancelText={__`cancel`}
      onConfirm={() => mutate()}
    >
      <Button
        type='text'
        danger
        size='small'
        loading={isLoading}
        icon={<DeleteOutlined style={{ color: colorTextTertiary }} />}
        aria-label={__`delete`}
        style={{ opacity: isVisible || isLoading ? 1 : 0 }}
      />
    </Popconfirm>
  )
}
